import { useMemo, useState } from 'react'
import { SquareRadical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { MATH_FUNCTIONS, MAX_ROUND_DIGITS, formulaToHuman, type MathFunction } from '@/lib/formula'
import {
  OperandInput,
  emptyOperand,
  serializeOperand,
  type ConditionOperand,
  type OperandState,
} from './ConditionBuilder'

const FN_LABELS: Record<MathFunction, string> = {
  LOG10: 'LOG10 — base-10 logarithm',
  ABS: 'ABS — absolute value',
  CEILING: 'CEILING — round up to a whole number',
  FLOOR: 'FLOOR — round down to a whole number',
  ROUND: 'ROUND — round to a number of decimals',
}

const FN_HINTS: Record<MathFunction, string> = {
  LOG10: 'Blank when the value is zero or negative — log₁₀ is undefined there.',
  ABS: 'The magnitude, with any minus sign dropped.',
  CEILING: 'Smallest whole number that is not less than the value (2.01 → 3).',
  FLOOR: 'Largest whole number that is not more than the value (2.99 → 2).',
  ROUND: `Rounds mid-formula, before any further arithmetic — separate from the Result format below. Halves round away from zero (2.345 → 2.35). Up to ${MAX_ROUND_DIGITS} decimal places.`,
}

interface Props {
  /** Fields (or library placeholders) offered in the operand picker. */
  operands: ConditionOperand[]
  /** Library mode: allow typing a brand-new placeholder name in the slot. */
  allowCustomRefs?: boolean
  /** Called with the complete, valid math expression. */
  onInsert: (formula: string) => void
  onCancel: () => void
  className?: string
}

/**
 * Structured LOG10 / ABS / CEILING / FLOOR / ROUND editor — mirrors
 * AggregateBuilder/DateFunctionBuilder's approach of only emitting the formula
 * once every slot is filled, so a half-built call never pollutes the stored
 * formula. The five functions share one panel (and one toolbar button) rather
 * than getting a button each: the operator row already carries IF/SUM/MAX/
 * TODAY/NOW/DATEADD/DATEDIFF.
 *
 * A whole sub-expression (the common case — `LOG10({pre} / {post})`) goes in
 * via the value slot's "Expression…" option, the same escape hatch
 * OperandInput offers everywhere else.
 */
export function MathFunctionBuilder({
  operands,
  allowCustomRefs,
  onInsert,
  onCancel,
  className,
}: Props) {
  const [fn, setFn] = useState<MathFunction>('LOG10')
  const [value, setValue] = useState<OperandState>(emptyOperand())
  const [digits, setDigits] = useState('2')

  // Ceiling mirrors the engine's own: past MAX_ROUND_DIGITS evaluateFormula
  // rounds the extra decimals away again, so a formula asking for them would
  // evaluate to blank. Block it here rather than let one be authored.
  const digitsValid =
    digits.trim() !== '' &&
    Number.isInteger(Number(digits)) &&
    Number(digits) >= 0 &&
    Number(digits) <= MAX_ROUND_DIGITS

  const serialized = useMemo(() => {
    const v = serializeOperand(value)
    if (!v) return null
    if (fn !== 'ROUND') return `${fn}(${v})`
    return digitsValid ? `ROUND(${v}, ${digits.trim()})` : null
  }, [fn, value, digits, digitsValid])

  const preview = useMemo(() => {
    if (!serialized) return null
    const labels: Record<string, string> = {}
    for (const o of operands) labels[o.id] = o.label
    // New placeholders aren't in the operand list — label them with their own name.
    for (const m of serialized.matchAll(/\{([^}]+)\}/g)) {
      if (!(m[1] in labels)) labels[m[1]] = m[1]
    }
    return formulaToHuman(serialized, labels)
  }, [serialized, operands])

  return (
    <div className={cn('space-y-2 rounded-md border bg-card p-2.5', className)}>
      <div className='flex items-center gap-1.5'>
        <SquareRadical className='h-3.5 w-3.5 text-muted-foreground' />
        <span className='text-xs font-semibold'>Build a math expression</span>
      </div>

      <div className='space-y-1.5'>
        <p className='text-[11px] text-muted-foreground'>Function</p>
        <Select value={fn} onValueChange={v => setFn(v as MathFunction)}>
          <SelectTrigger className='h-8 text-xs'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MATH_FUNCTIONS.map(f => (
              <SelectItem key={f} value={f}>
                {FN_LABELS[f]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className='text-[11px] text-muted-foreground'>{FN_HINTS[fn]}</p>
      </div>

      <div className='space-y-1.5'>
        <p className='text-[11px] text-muted-foreground'>
          Value — pick a field, or choose "Expression…" and type a calculation such as{' '}
          <span className='font-mono'>{'{pre} / {post}'}</span>.
        </p>
        <OperandInput
          value={value}
          onChange={setValue}
          operands={operands}
          allowCustomRefs={allowCustomRefs}
          placeholder='Value…'
        />
      </div>

      {fn === 'ROUND' && (
        <div className='space-y-1.5'>
          <p className='text-[11px] text-muted-foreground'>Decimal places</p>
          <Input
            type='number'
            min={0}
            max={MAX_ROUND_DIGITS}
            step={1}
            value={digits}
            onChange={e => setDigits(e.target.value)}
            className='h-8 text-sm'
          />
          {!digitsValid && (
            <p className='text-[11px] text-destructive'>
              Enter a whole number of decimal places, 0 to {MAX_ROUND_DIGITS}.
            </p>
          )}
        </div>
      )}

      {preview ? (
        <div className='rounded bg-muted/40 px-2 py-1.5'>
          <p className='text-[10px] uppercase tracking-wide text-muted-foreground'>Preview</p>
          <p className='text-xs font-medium break-words'>{preview}</p>
        </div>
      ) : (
        <p className='text-[11px] text-muted-foreground'>Fill every value to enable Insert.</p>
      )}

      <div className='flex items-center justify-end gap-2'>
        <Button type='button' size='sm' variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type='button'
          size='sm'
          disabled={!serialized}
          onClick={() => serialized && onInsert(serialized)}
        >
          Insert {fn.toLowerCase()}
        </Button>
      </div>
    </div>
  )
}
